import { graphql } from '@/__generated__';

export const TestLevelBySlugQuery = graphql(`
  query TestLevelBySlugQuery($slug: String!) {
    testLevelBySlug(slug: $slug) {
      ...TestLevel
    }
  }
`);

export const TestLevelQuery = graphql(`
  query TestLevelQuery($id: ID!) {
    testLevel(id: $id) {
      id: testLevelId
      slug
      name
      playlistId
      description
      descriptionEn
    }
  }
`);

export const TestLevelFragment = graphql(/* GraphQL */ `
  fragment TestLevel on TestLevel {
    id: testLevelId
    slug
    name
    playlistId
    typeTestLevel
    description
    descriptionEn
  }
  fragment UserError on UserError {
    message
    code
    field
    param {
      value
    }
  }
  fragment LearnerAchievement on LearnerAchievement {
    id
    learnerAchievementId
    name
    tier
    level
    maxLevel
    value
    nextLevelValue
    seen
    dateReceived
  }
`);

export const TestLevelsWithFilter = graphql(`
  query TestLevelsWithFilter($filterBy: TestLevelFilter) {
    testLevels(first: 50, filterBy: $filterBy) {
      nodes {
        ...TestLevel
      }
    }
  }
`);

export const ProfileFragment = graphql(/* GraphQL */ `
  fragment learnerProfileRanking on LearnerProfile {
    id
    learnerProfileId
    school {
      id
      schoolId
      name
      province {
        id
        provinceId
        name
      }
    }
    displayName
    avatarUrl
    frameImage {
      id
      url
    }
    gender
    dateOfBirth
  }
`);

export const GetTestLevelRanking = graphql(`
  query GetTestLevelRanking(
    $frequency: RankFrequency!
    $testLevelId: ID!
    $classCode: String
    $first: Int
    $last: Int
    $before: Cursor
    $after: Cursor
    $filterBy: LearnerTestLevelRankFilter
    $filterBy2: LearnerTestLevelRankFilter
    $includeProfileRank: Boolean!
  ) {
    learnerTestLevelRanks(
      frequency: $frequency
      testLevelId: $testLevelId
      classCode: $classCode
      filterBy: $filterBy
      first: $first
      last: $last
      before: $before
      after: $after
    ) {
      edges {
        cursor
        node {
          rank
          score
          learnerProfile {
            ...learnerProfileRanking
          }
          grade
        }
      }
      totalCount
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
        startCursor
      }
    }
    profileLearnerTestLevelRanks: learnerTestLevelRanks(
      frequency: $frequency
      testLevelId: $testLevelId
      classCode: $classCode
      filterBy: $filterBy2
      first: 1
    ) @include(if: $includeProfileRank) {
      edges {
        cursor
        node {
          rank
          score
          learnerProfile {
            ...learnerProfileRanking
          }
          grade
        }
      }
    }
  }
`);

export const GetChallengeRanking = graphql(`
  query LearnerChallengeRanks(
    $challengeCode: String!
    $first: Int
    $after: Cursor
    $last: Int
    $before: Cursor
    $filterBy: LearnerChallengeRankFilter
    $filterBy2: LearnerChallengeRankFilter
    $includeProfileRank: Boolean!
  ) {
    learnerChallengeRanks(
      challengeCode: $challengeCode
      first: $first
      after: $after
      last: $last
      before: $before
      filterBy: $filterBy
    ) {
      totalCount
      edges {
        cursor
        node {
          id
          learnerChallengeRankId
          grade
          rank
          score
          learnerProfile {
            ...learnerProfileRanking
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
        startCursor
      }
      totalCount
    }
    profileLearnerChallengeRank: learnerChallengeRanks(
      challengeCode: $challengeCode
      first: 1
      filterBy: $filterBy2
    ) @include(if: $includeProfileRank) {
      totalCount
      edges {
        cursor
        node {
          id
          learnerChallengeRankId
          grade
          rank
          score
          learnerProfile {
            ...learnerProfileRanking
          }
        }
      }
    }
  }
`);

export const GetSchoolRanking = graphql(`
  query SchoolTestLevelRanks(
    $frequency: RankFrequency!
    $testLevelId: ID!
    $first: Int
    $after: Cursor
    $last: Int
    $before: Cursor
    $filterBy: SchoolTestLevelRankFilter
  ) {
    schoolTestLevelRanks(
      frequency: $frequency
      testLevelId: $testLevelId
      first: $first
      after: $after
      last: $last
      before: $before
      filterBy: $filterBy
    ) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        node {
          id
          schoolTestLevelRankId
          school {
            id
            name
            schoolId
            province {
              id
              name
              provinceId
            }
          }
          totalGrade
          totalScore
          avgAccuracy
          rank
        }
        cursor
      }
    }
  }
`);

export const LearnerProfileById = graphql(`
  query LearnerProfileById(
    $learnerProfileId: ID!
    $first: Int
    $after: Cursor
    $last: Int
    $before: Cursor
    $filterBy: UserAttemptFilter
  ) {
    learnerProfile(id: $learnerProfileId) {
      id
      learnerProfileId
      displayName
      avatarUrl
      frameImage {
        id
        url
      }
      achievements {
        ...LearnerAchievement
      }
      attemptStats {
        totalGrade
        accuracy
        studyTime
        completedTest
      }
      userAttempts(
        first: $first
        after: $after
        last: $last
        before: $before
        filterBy: $filterBy
      ) {
        edges {
          node {
            id
            submittedAt
            testLevel {
              id
              name
            }
            checkPoint {
              id
              name
              nameEn
            }
            executionTime
            grade
            maxGrade
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          hasPreviousPage
          startCursor
        }
      }
      coin
      star
      wisdomStoneBalance
      classStudent {
        id
        class {
          id
          classId
        }
      }
    }
  }
`);
